import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormControl,
  Grid,
  Paper,
  Step,
  StepLabel,
  Stepper,
  InputLabel,
  Link,
  MenuItem,
  Stack,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { Icon } from "@iconify/react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Logo from "../components/logo/Logo";
import Loading from "../components/spinners/Loading";
import { serverURL } from "../app/api/axios/http.service";
import {
  createPublicRegistration,
  getPublicSiteRegistration,
  validatePublicDiscountCode,
} from "../app/api/axios/services/public.service";
import PublicMemberForm from "../sections/@dashboard/members/PublicMemberForm";
import NewContractForm from "../sections/@dashboard/members/NewContractForm";
import {
  getNewMemberPayedTillValue,
  gradArray,
} from "../components/form/fields/inputFields";
import {
  getOpeningDayLabel,
  normalizeDayValues,
  sortOpeningHoursByDay,
  sortTrainingSessions,
} from "../utils/openingHours";

const steps = {
  SELECT_TYPE: 0,
  MEMBER_FORM: 1,
  ADD_ANOTHER: 2,
  CONTRACT_FORM: 3,
  DOCUMENTS: 4,
  SUMMARY: 5,
  FINISH: 6,
};

const getDisplayDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("de-DE");
};

const formatMoney = (value) => Number(value || 0).toFixed(2);
const formatBodyHeight = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "Nicht hinterlegt";
  return `ca. ${Math.round(numeric)} cm`;
};

const memberTypeOptions = [
  {
    value: "adult",
    label: "Volljähriges Mitglied",
    description:
      "Das Mitglied ist mindestens 18 Jahre alt und unterschreibt den Vertrag selbst.",
    icon: "mdi:account",
    ageHint: "Ab 18 Jahren",
  },
  {
    value: "child",
    label: "Kind / minderjähriges Mitglied",
    description:
      "Ein Elternteil oder Erziehungsberechtigter wird als Vertragsnehmer eingetragen.",
    icon: "mdi:account-child",
    ageHint: "Unter 18 Jahren",
  },
];

function PublicRegistrationPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { siteId } = useParams();
  const [site, setSite] = useState(null);
  const [memberType, setMemberType] = useState(null);
  const [members, setMembers] = useState([]);
  const [currentMember, setCurrentMember] = useState({});
  const [contractData, setContractData] = useState({});
  const [step, setStep] = useState(steps.SELECT_TYPE);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);
  const [docConsents, setDocConsents] = useState({});
  const [typedName, setTypedName] = useState("");
  const [signatureChecked, setSignatureChecked] = useState(false);
  const [consentError, setConsentError] = useState(null);
  const [discountCodes, setDiscountCodes] = useState([
    {
      code: "",
      status: "idle",
      applyTo: "contribution",
      discountType: "percent",
      discountValue: 0,
      message: "",
    },
  ]);
  const discountTimers = useRef({});
  const pageTopRef = useRef(null);

  const publicOptions = useMemo(() => {
    if (!site) return [];
    if (
      site.publicRegistrationOptions &&
      site.publicRegistrationOptions.length
    ) {
      return site.publicRegistrationOptions;
    }
    return [
      {
        publicContribution: site.memberContribution,
        publicContractDuration: site.contractDuration,
      },
    ];
  }, [site]);

  const stepLabels = useMemo(
    () => [
      "Mitgliedsart",
      "Mitgliedsdaten",
      "Vertragsdaten",
      "Dokumente",
      "Zusammenfassung",
    ],
    [],
  );

  const activeStepIndex = useMemo(() => {
    if (step === steps.SELECT_TYPE) return 0;
    if (step === steps.MEMBER_FORM || step === steps.ADD_ANOTHER) return 1;
    if (step === steps.CONTRACT_FORM) return 2;
    if (step === steps.DOCUMENTS) return 3;
    if (step === steps.SUMMARY || step === steps.FINISH) return 4;
    return 0;
  }, [step]);

  const siteAddress = useMemo(() => {
    if (!site) return "";
    const explicitAddress = String(site.publicAddress || "").trim();
    if (explicitAddress) return explicitAddress;
    const firstLine = String(site.address || site.street || "").trim();
    const secondLine = [site.zip, site.city].filter(Boolean).join(" ").trim();
    return [firstLine, secondLine].filter(Boolean).join(", ");
  }, [site]);

  const siteOpeningHours = useMemo(() => {
    if (!site) return [];
    const publicOpening = Array.isArray(site.publicOpeningHours)
      ? site.publicOpeningHours
          .map((entry) => String(entry || "").trim())
          .filter(Boolean)
      : [];
    if (publicOpening.length) return publicOpening;
    return sortOpeningHoursByDay(site.openingHours || []).map(
      (entry) =>
        `${getOpeningDayLabel(entry.day)}: ${entry.start} - ${entry.end}`,
    );
  }, [site]);

  const siteTrainingTimes = useMemo(() => {
    if (!site) return [];
    const publicTraining = Array.isArray(site.publicTrainingTimes)
      ? site.publicTrainingTimes
          .map((entry) => String(entry || "").trim())
          .filter(Boolean)
      : [];
    if (publicTraining.length) return publicTraining;
    return sortTrainingSessions(site.trainingSessions || []).map((entry) => {
      const normalizedDays = normalizeDayValues(entry?.days ?? entry?.day);
      const dayLabel =
        normalizedDays.length > 0
          ? normalizedDays.map(getOpeningDayLabel).join(", ")
          : getOpeningDayLabel(entry?.day);
      const titlePart = entry?.title ? `${entry.title}: ` : "";
      return `${dayLabel} · ${titlePart}${entry.start} - ${entry.end}`;
    });
  }, [site]);

  const siteInfoText = useMemo(
    () => String(site?.publicInfoText || "").trim(),
    [site?.publicInfoText],
  );

  const siteContactItems = useMemo(() => {
    if (!site) return [];
    const items = [];
    if (site.phone) {
      items.push(`Telefon: ${site.phone}`);
    }
    if (site.mail) {
      items.push(`E-Mail: ${site.mail}`);
    }
    if (site.homepage) {
      items.push(`Website: ${site.homepage}`);
    }
    return items;
  }, [site]);

  const publicRegistrationDocuments = useMemo(
    () => site?.publicRegistrationDocuments || [],
    [site],
  );

  const readOnlyFields = useMemo(
    () => [
      "memberNumber",
      "contribution",
      "contractDuration",
      "contractExtension",
      "noticePeriod",
      "registrationFee",
      "payedTill",
    ],
    [],
  );

  const discountSummary = useMemo(() => {
    const validEntries = discountCodes.filter(
      (entry) => entry.status === "valid" && entry.discountValue > 0,
    );
    const uniqueByCode = new Map();
    validEntries.forEach((entry) => {
      if (!uniqueByCode.has(entry.code)) {
        uniqueByCode.set(entry.code, entry);
      }
    });
    const totals = Array.from(uniqueByCode.values()).reduce(
      (acc, entry) => {
        const target =
          entry.applyTo === "enrollmentFee" ? "enrollmentFee" : "contribution";
        if (entry.discountType === "amount") {
          acc[target].totalAmount += entry.discountValue;
        } else {
          acc[target].totalPercent += entry.discountValue;
        }
        return acc;
      },
      {
        contribution: { totalPercent: 0, totalAmount: 0 },
        enrollmentFee: { totalPercent: 0, totalAmount: 0 },
      },
    );
    return {
      codes: Array.from(uniqueByCode.keys()),
      contribution: {
        totalPercent: Math.min(totals.contribution.totalPercent, 100),
        totalAmount: Math.max(totals.contribution.totalAmount, 0),
      },
      enrollmentFee: {
        totalPercent: Math.min(totals.enrollmentFee.totalPercent, 100),
        totalAmount: Math.max(totals.enrollmentFee.totalAmount, 0),
      },
    };
  }, [discountCodes]);

  const formatDiscountLabel = (summary) => {
    if (!summary) return "";
    const parts = [];
    if (summary.totalPercent > 0) {
      parts.push(`${summary.totalPercent}%`);
    }
    if (summary.totalAmount > 0) {
      parts.push(`${formatMoney(summary.totalAmount)} EUR`);
    }
    return parts.join(" + ");
  };

  const pricingPreview = useMemo(() => {
    if (!site) {
      return {
        baseContribution: 0,
        baseEnrollmentFee: 0,
        discountedContribution: 0,
        discountedEnrollmentFee: 0,
      };
    }
    const selectedOption = publicOptions[selectedOptionIndex];
    const baseContribution =
      Number(selectedOption?.publicContribution ?? site.memberContribution) ||
      0;
    const baseEnrollmentFee = Number(site.enrollmentFee) || 0;
    const discountedContribution = Math.max(
      0,
      Number(
        (
          baseContribution *
            (1 - discountSummary.contribution.totalPercent / 100) -
          discountSummary.contribution.totalAmount
        ).toFixed(2),
      ),
    );
    const discountedEnrollmentFee = Math.max(
      0,
      Number(
        (
          baseEnrollmentFee *
            (1 - discountSummary.enrollmentFee.totalPercent / 100) -
          discountSummary.enrollmentFee.totalAmount
        ).toFixed(2),
      ),
    );
    return {
      baseContribution,
      baseEnrollmentFee,
      discountedContribution,
      discountedEnrollmentFee,
    };
  }, [site, publicOptions, selectedOptionIndex, discountSummary]);

  const contributionDiscountLabel = formatDiscountLabel(
    discountSummary.contribution,
  );
  const enrollmentFeeDiscountLabel = formatDiscountLabel(
    discountSummary.enrollmentFee,
  );

  const memberDefaults = useMemo(() => {
    if (!site) return {};
    const nextNumber = (site.latestMemberNumber || 0) + members.length;
    return {
      memberNumber: `${site.memberIdentifier || ""}${nextNumber}`,
      contribution: pricingPreview.discountedContribution,
      contractDuration:
        publicOptions[selectedOptionIndex]?.publicContractDuration ??
        site.contractDuration,
      contractExtension: site.contractExtension,
      noticePeriod: site.noticePeriod,
      registrationFee: pricingPreview.discountedEnrollmentFee,
      degree: 0,
      payRegistrationFee: true,
      payedTill: getNewMemberPayedTillValue(),
    };
  }, [
    site,
    members.length,
    publicOptions,
    selectedOptionIndex,
    pricingPreview,
  ]);

  useEffect(() => {
    if (!publicOptions.length) return;
    if (selectedOptionIndex >= publicOptions.length) {
      setSelectedOptionIndex(0);
    }
  }, [publicOptions.length, selectedOptionIndex]);

  useEffect(() => {
    let isMounted = true;
    const fetchSite = async () => {
      try {
        setLoading(true);
        const response = await getPublicSiteRegistration(siteId);
        if (isMounted) {
          setSite(response?.data?.site || null);
        }
      } catch (err) {
        console.error("Failed to load site:", err);
        if (isMounted) {
          setError("Standort konnte nicht geladen werden.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (siteId) {
      fetchSite();
    }

    return () => {
      isMounted = false;
    };
  }, [siteId]);

  useEffect(() => {
    if (!publicRegistrationDocuments.length) {
      setDocConsents({});
      return;
    }
    const initial = {};
    publicRegistrationDocuments.forEach((doc) => {
      if (doc?.docId) {
        initial[doc.docId] = false;
      }
    });
    setDocConsents(initial);
  }, [publicRegistrationDocuments]);

  useEffect(() => {
    const timers = discountTimers.current;
    return () => {
      Object.values(timers).forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const scrollToTopOnMobile = () => {
    if (!isMobile || typeof window === "undefined") return;

    const scrollToTop = () => {
      pageTopRef.current?.scrollIntoView({ block: "start", behavior: "auto" });
      const scrollingElement =
        document.scrollingElement || document.documentElement || document.body;
      if (scrollingElement) {
        scrollingElement.scrollTop = 0;
      }
      window.scrollTo(0, 0);
    };

    scrollToTop();
    requestAnimationFrame(scrollToTop);
    setTimeout(scrollToTop, 60);
  };

  const goToStep = (nextStep) => {
    setStep(nextStep);
    scrollToTopOnMobile();
  };

  const handleSelectMemberType = (type) => {
    if (!type) return;
    setMemberType(type);
    setMembers([]);
    setCurrentMember({});
    setContractData({});
    const resetConsents = {};
    publicRegistrationDocuments.forEach((doc) => {
      if (doc?.docId) {
        resetConsents[doc.docId] = false;
      }
    });
    setDocConsents(resetConsents);
    setTypedName("");
    setSignatureChecked(false);
    setConsentError(null);
    setDiscountCodes([
      {
        code: "",
        status: "idle",
        applyTo: "contribution",
        discountType: "percent",
        discountValue: 0,
        message: "",
      },
    ]);
    goToStep(steps.MEMBER_FORM);
  };

  const handleMemberDataSubmit = (memberData) => {
    const selectedOption = publicOptions[selectedOptionIndex];
    const parsedBodyHeightCm = Number(memberData?.bodyHeightCm);
    const normalizedMemberData = {
      ...memberData,
      bodyHeightCm: Number.isFinite(parsedBodyHeightCm)
        ? Math.round(parsedBodyHeightCm)
        : memberData?.bodyHeightCm,
      contribution: memberDefaults.contribution ?? memberData.contribution,
      contractDuration:
        selectedOption?.publicContractDuration ?? memberData.contractDuration,
      contractExtension: site?.contractExtension,
      noticePeriod: site?.noticePeriod,
      registrationFee:
        memberDefaults.registrationFee ?? memberData.registrationFee,
      degree: memberData.degree ?? memberDefaults.degree ?? 0,
      payRegistrationFee: true,
    };

    setMembers((prev) => [...prev, normalizedMemberData]);
    setCurrentMember({});

    if (memberType === "child") {
      goToStep(steps.ADD_ANOTHER);
      return;
    }

    goToStep(steps.CONTRACT_FORM);
  };

  const handleAddAnotherChild = () => {
    setCurrentMember({});
    goToStep(steps.MEMBER_FORM);
  };

  const handleContinueToContract = () => {
    goToStep(steps.CONTRACT_FORM);
  };

  const handleContractDataSubmit = (data) => {
    setContractData(data);
    goToStep(steps.DOCUMENTS);
  };

  const handleBack = () => {
    if (step === steps.MEMBER_FORM) {
      setMemberType(null);
      setMembers([]);
      setCurrentMember({});
      setContractData({});
      setStep(steps.SELECT_TYPE);
      return;
    }
    if (step === steps.ADD_ANOTHER) {
      const updatedMembers = [...members];
      const lastMember = updatedMembers.pop();
      setMembers(updatedMembers);
      setCurrentMember(lastMember || {});
      setStep(steps.MEMBER_FORM);
      return;
    }
    if (step === steps.CONTRACT_FORM) {
      if (memberType === "child") {
        setStep(steps.ADD_ANOTHER);
      } else {
        const [first] = members;
        setCurrentMember(first || {});
        setMembers(first ? [first] : []);
        setStep(steps.MEMBER_FORM);
      }
      return;
    }
    if (step === steps.DOCUMENTS) {
      setStep(steps.CONTRACT_FORM);
      return;
    }
    if (step === steps.SUMMARY) {
      setStep(steps.DOCUMENTS);
      return;
    }
  };

  const getDocumentUrl = (doc) => {
    if (!doc) return "";
    if (doc.url) return doc.url;
    if (doc.storageKey) {
      return `${serverURL()}/public/files/${doc.storageKey}`;
    }
    return "";
  };

  const documentLinks = useMemo(
    () =>
      publicRegistrationDocuments.map((doc, index) => ({
        key: doc.docId || `doc-${index}`,
        label: doc.title || doc.fileName || `Dokument ${index + 1}`,
        doc,
      })),
    [publicRegistrationDocuments],
  );

  const missingDocumentLabels = useMemo(
    () =>
      documentLinks
        .filter((item) => !getDocumentUrl(item.doc))
        .map((item) => item.label),
    [documentLinks],
  );

  const handleConsentToggle = (docId) => (event) => {
    setDocConsents((prev) => ({ ...prev, [docId]: event.target.checked }));
  };

  const updateDiscountEntry = (index, updates) => {
    setDiscountCodes((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, ...updates } : entry)),
    );
  };

  const handleDiscountCodeChange = (index, value) => {
    const formatted = value.toUpperCase();
    updateDiscountEntry(index, {
      code: formatted,
      status: formatted ? "typing" : "idle",
      applyTo: "contribution",
      discountType: "percent",
      discountValue: 0,
      message: "",
    });

    if (discountTimers.current[index]) {
      clearTimeout(discountTimers.current[index]);
    }

    if (!formatted) return;

    discountTimers.current[index] = setTimeout(async () => {
      updateDiscountEntry(index, { status: "loading", message: "" });
      try {
        const response = await validatePublicDiscountCode(siteId, formatted);
        const valid = Boolean(response?.data?.valid);
        const responseType = String(
          response?.data?.discountType ||
            (Number.isFinite(Number(response?.data?.discountPercent))
              ? "percent"
              : "amount"),
        );
        const discountValue = Number(
          response?.data?.discountValue ??
            (responseType === "percent"
              ? response?.data?.discountPercent
              : response?.data?.discountAmount) ??
            0,
        );
        const responseApplyTo =
          response?.data?.applyTo === "enrollmentFee"
            ? "enrollmentFee"
            : "contribution";
        setDiscountCodes((prev) => {
          if (prev[index]?.code !== formatted) return prev;
          return prev.map((entry, i) =>
            i === index
              ? {
                  ...entry,
                  status: valid ? "valid" : "invalid",
                  applyTo: responseApplyTo,
                  discountType: responseType,
                  discountValue: valid
                    ? Math.max(
                        0,
                        responseType === "percent"
                          ? Math.min(discountValue, 100)
                          : discountValue,
                      )
                    : 0,
                  message: valid
                    ? `Rabatt ${
                        responseType === "percent"
                          ? `${Math.min(discountValue, 100)}%`
                          : `${formatMoney(discountValue)} EUR`
                      } auf ${
                        responseApplyTo === "enrollmentFee"
                          ? "Anmeldegebühr"
                          : "Beitrag"
                      } aktiviert`
                    : "Rabattcode ungültig",
                }
              : entry,
          );
        });
      } catch (err) {
        setDiscountCodes((prev) => {
          if (prev[index]?.code !== formatted) return prev;
          return prev.map((entry, i) =>
            i === index
              ? {
                  ...entry,
                  status: "error",
                  applyTo: "contribution",
                  discountType: "percent",
                  discountValue: 0,
                  message: "Rabattcode konnte nicht geprüft werden",
                }
              : entry,
          );
        });
      }
    }, 500);
  };

  const handleAddDiscountCode = () => {
    if (discountCodes.length >= 3) return;
    setDiscountCodes((prev) => [
      ...prev,
      {
        code: "",
        status: "idle",
        applyTo: "contribution",
        discountType: "percent",
        discountValue: 0,
        message: "",
      },
    ]);
  };

  const handleRemoveDiscountCode = (index) => {
    if (discountTimers.current[index]) {
      clearTimeout(discountTimers.current[index]);
    }
    setDiscountCodes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitRegistration = async () => {
    if (submitting) return;
    try {
      if (missingDocumentLabels.length) {
        setConsentError(
          "Pflichtdokumente fehlen. Bitte versuchen Sie es später erneut.",
        );
        goToStep(steps.DOCUMENTS);
        return;
      }
      const missingConsents = publicRegistrationDocuments.filter(
        (doc) => doc?.docId && !docConsents[doc.docId],
      );
      if (!typedName.trim() || !signatureChecked || missingConsents.length) {
        setConsentError(
          "Bitte alle Pflichtzustimmungen bestätigen und den Namen unterschreiben.",
        );
        goToStep(steps.DOCUMENTS);
        return;
      }

      setSubmitting(true);
      setError(null);
      setConsentError(null);
      const result = await createPublicRegistration(siteId, {
        memberType,
        members,
        contractData,
        publicOption: publicOptions[selectedOptionIndex],
        discountCodes: discountSummary.codes,
        consents: publicRegistrationDocuments
          .filter((doc) => doc?.docId)
          .map((doc) => ({
            docId: doc.docId,
            accepted: Boolean(docConsents[doc.docId]),
          })),
        signature: {
          typedName,
          signatureCheckbox: signatureChecked,
        },
      });
      if (result?.status === 201) {
        setSuccess(true);
        goToStep(steps.FINISH);
      }
    } catch (err) {
      console.error("Registration error:", err);
      setError("Registrierung konnte nicht gesendet werden.");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Early returns ───────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          background:
            "linear-gradient(135deg, rgba(24, 24, 24, 0.06), rgba(255, 255, 255, 0.9))",
        }}
      >
        <Loading />
        <Typography variant="body2" color="text.secondary">
          Anmeldeformular wird geladen…
        </Typography>
      </Box>
    );
  }

  if (error && !site) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          background:
            "linear-gradient(135deg, rgba(24, 24, 24, 0.06), rgba(255, 255, 255, 0.9))",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 440,
            width: "100%",
            textAlign: "center",
            borderRadius: 3,
          }}
        >
          <Icon
            icon="mdi:alert-circle-outline"
            width={52}
            color={theme.palette.error.main}
          />
          <Typography variant="h5" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
            Fehler beim Laden
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!site) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          background:
            "linear-gradient(135deg, rgba(24, 24, 24, 0.06), rgba(255, 255, 255, 0.9))",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 440,
            width: "100%",
            textAlign: "center",
            borderRadius: 3,
          }}
        >
          <Icon
            icon="mdi:map-marker-off-outline"
            width={52}
            color={theme.palette.text.disabled}
          />
          <Typography variant="h5" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
            Standort nicht gefunden
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Dieser Anmeldelink ist leider nicht gültig oder abgelaufen.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, sm: 3 },
          background:
            "linear-gradient(135deg, rgba(24, 24, 24, 0.06), rgba(255, 255, 255, 0.9))",
        }}
      >
        <Helmet>
          <title> MA | Registrierung abgeschlossen</title>
        </Helmet>
        <Paper
          elevation={4}
          sx={{
            p: { xs: 3, sm: 5 },
            maxWidth: 560,
            width: "100%",
            borderRadius: 3,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: "success.main",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
            }}
          >
            <Icon icon="mdi:check" width={48} />
          </Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
            Vielen Dank!
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            Ihre Anmeldung wurde erfolgreich gesendet.
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Stack spacing={2} sx={{ textAlign: "left" }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Icon
                icon="mdi:clock-check-outline"
                width={22}
                color={theme.palette.success.main}
                style={{ marginTop: 2, flexShrink: 0 }}
              />
              <Typography variant="body1">
                Die Verwaltung prüft Ihre Angaben und meldet sich in Kürze bei
                Ihnen.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Icon
                icon="mdi:email-check-outline"
                width={22}
                color={theme.palette.success.main}
                style={{ marginTop: 2, flexShrink: 0 }}
              />
              <Typography variant="body1">
                Sie erhalten eine Bestätigungsmail mit einer Kopie Ihrer
                Angaben.
              </Typography>
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Sie können dieses Fenster jetzt schließen.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // ─── Layout constants ─────────────────────────────────────────────────────────

  const contentWidthSx = {
    width: "100%",
    maxWidth: { xs: 360, sm: 1100 },
    mx: "auto",
  };

  const stepContextInfo = {
    [steps.MEMBER_FORM]:
      memberType === "child"
        ? "Bitte tragen Sie die persönlichen Daten des Kindes ein."
        : "Bitte tragen Sie Ihre persönlichen Daten ein.",
    [steps.CONTRACT_FORM]:
      memberType === "child"
        ? "Als Vertragsnehmer tragen Sie sich als Erziehungsberechtigter ein — nicht das Kind."
        : "Bitte füllen Sie Ihre Kontakt- und Bankdaten aus.",
    [steps.DOCUMENTS]:
      "Bitte lesen Sie die Dokumente und bestätigen Sie Ihre Einwilligung.",
    [steps.SUMMARY]:
      "Überprüfen Sie Ihre Angaben, bevor Sie die Anmeldung absenden.",
  };

  // ─── Main render ──────────────────────────────────────────────────────────────

  return (
    <Box
      ref={pageTopRef}
      sx={{
        px: { xs: 1, sm: 2, md: 4 },
        py: { xs: 2, md: 4 },
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, rgba(24, 24, 24, 0.06), rgba(255, 255, 255, 0.9))",
      }}
    >
      <Helmet>
        <title> MA | Mitgliedsregistrierung</title>
      </Helmet>

      {/* ── Page header with stepper ── */}
      <Paper
        elevation={3}
        sx={{
          ...contentWidthSx,
          p: { xs: 1.5, sm: 2.5, md: 3 },
          mb: 2,
          borderRadius: 3,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 2, md: 2 }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
        >
          <Stack spacing={0.75}>
            <Logo
              disabledLink
              logoPath={`${serverURL()}/public/tenant/image/${
                site?.tenant ?? " "
              }`}
              sx={{ width: { xs: "110px", md: "160px" }, height: "auto" }}
            />
            <Box>
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
                Online-Anmeldung
              </Typography>
              <Typography
                variant={isMobile ? "body2" : "body1"}
                color="text.secondary"
              >
                {site.siteName}
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ width: { xs: "100%", md: "55%" } }}>
            <Stepper
              activeStep={activeStepIndex}
              alternativeLabel
              orientation="horizontal"
              sx={{
                px: { xs: 0.25, sm: 0.5 },
                "& .MuiStep-root": { px: { xs: 0.2, sm: 1 } },
                "& .MuiStepLabel-iconContainer": { pr: { xs: 0, sm: 1 } },
                "& .MuiStepLabel-label": {
                  display: { xs: "none", sm: "block" },
                  fontSize: { sm: "0.92rem", md: "0.95rem" },
                },
                "& .MuiStepIcon-root": {
                  fontSize: { xs: "1.35rem", sm: "1.55rem" },
                },
              }}
            >
              {stepLabels.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {isMobile && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.75 }}
              >
                Schritt {activeStepIndex + 1} von {stepLabels.length}:{" "}
                {stepLabels[activeStepIndex]}
              </Typography>
            )}
          </Box>
        </Stack>
      </Paper>

      {/* ── Back button + contextual step hint ── */}
      {step !== steps.SELECT_TYPE && (
        <Box sx={{ ...contentWidthSx, mb: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleBack}
              startIcon={<Icon icon="mdi:arrow-left" width={18} />}
              fullWidth={isMobile}
              sx={{ maxWidth: isMobile ? "none" : 160 }}
            >
              Zurück
            </Button>
            {stepContextInfo[step] && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                {stepContextInfo[step]}
              </Typography>
            )}
          </Stack>
        </Box>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* STEP 0 — SELECT_TYPE                                                  */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {step === steps.SELECT_TYPE && (
        <Stack spacing={2} sx={contentWidthSx}>
          {/* Welcome banner */}
          <Paper elevation={3} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  fontWeight={700}
                  sx={{ mb: 0.25 }}
                >
                  Willkommen bei der Online-Anmeldung
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Melden Sie sich bei <strong>{site.siteName}</strong> an — die
                  Anmeldung dauert nur wenige Minuten.
                </Typography>
              </Box>

              <Alert
                severity="success"
                icon={<Icon icon="mdi:shield-check-outline" width={18} />}
                sx={{ py: 0.25 }}
              >
                <Typography variant="caption">
                  <strong>Sichere Übertragung.</strong> Ihre Daten werden
                  verschlüsselt übermittelt (DSGVO-konform).
                </Typography>
              </Alert>
            </Stack>
          </Paper>

          {/* Site & business info — compact accordions */}
          {(siteAddress ||
            siteTrainingTimes.length > 0 ||
            siteOpeningHours.length > 0 ||
            siteContactItems.length > 0 ||
            siteInfoText ||
            site?.tenantInfo) && (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                "& .MuiAccordion-root": {
                  boxShadow: "none",
                  "&:before": { display: "none" },
                  "&.Mui-expanded": { m: 0 },
                },
                "& .MuiAccordionSummary-root": {
                  minHeight: 48,
                  px: 2,
                  "&.Mui-expanded": { minHeight: 48 },
                },
                "& .MuiAccordionSummary-content": {
                  my: 1,
                  "&.Mui-expanded": { my: 1 },
                },
                "& .MuiAccordionDetails-root": { px: 2, pt: 0, pb: 2 },
              }}
            >
              {/* Standortinformationen header */}
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Icon
                    icon="mdi:information-outline"
                    width={20}
                    color={theme.palette.primary.main}
                  />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Standort- & Geschäftsinformationen
                  </Typography>
                </Stack>
              </Box>

              {siteAddress && (
                <Accordion disableGutters>
                  <AccordionSummary
                    expandIcon={<Icon icon="mdi:chevron-down" width={22} />}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon
                          icon="mdi:map-marker-outline"
                          width={18}
                          color={theme.palette.primary.main}
                        />
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        Trainings-Adresse
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary">
                      {siteAddress}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}

              {siteTrainingTimes.length > 0 && (
                <Accordion disableGutters>
                  <AccordionSummary
                    expandIcon={<Icon icon="mdi:chevron-down" width={22} />}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.info.main, 0.08),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon
                          icon="mdi:clock-outline"
                          width={18}
                          color={theme.palette.info.main}
                        />
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        Trainingszeiten
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={0.25}>
                      {siteTrainingTimes.map((entry, index) => (
                        <Typography
                          key={`training-${index}`}
                          variant="body2"
                          color="text.secondary"
                        >
                          {entry}
                        </Typography>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}

              {siteOpeningHours.length > 0 && (
                <Accordion disableGutters>
                  <AccordionSummary
                    expandIcon={<Icon icon="mdi:chevron-down" width={22} />}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.warning.main, 0.08),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon
                          icon="mdi:calendar-clock"
                          width={18}
                          color={theme.palette.warning.main}
                        />
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        Öffnungszeiten
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={0.25}>
                      {siteOpeningHours.map((entry, index) => (
                        <Typography
                          key={`opening-${index}`}
                          variant="body2"
                          color="text.secondary"
                        >
                          {entry}
                        </Typography>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}

              {siteContactItems.length > 0 && (
                <Accordion disableGutters>
                  <AccordionSummary
                    expandIcon={<Icon icon="mdi:chevron-down" width={22} />}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.success.main, 0.08),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon
                          icon="mdi:phone-outline"
                          width={18}
                          color={theme.palette.success.main}
                        />
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        Kontakt
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={0.75}>
                      {site.phone && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Icon
                            icon="mdi:phone-outline"
                            width={16}
                            color={theme.palette.text.secondary}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="a"
                            href={`tel:${site.phone}`}
                            sx={{
                              textDecoration: "none",
                              color: "inherit",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {site.phone}
                          </Typography>
                        </Stack>
                      )}
                      {site.mail && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Icon
                            icon="mdi:email-outline"
                            width={16}
                            color={theme.palette.text.secondary}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="a"
                            href={`mailto:${site.mail}`}
                            sx={{
                              textDecoration: "none",
                              color: "inherit",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {site.mail}
                          </Typography>
                        </Stack>
                      )}
                      {site.homepage && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Icon
                            icon="mdi:web"
                            width={16}
                            color={theme.palette.text.secondary}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="a"
                            href={
                              site.homepage.startsWith("http")
                                ? site.homepage
                                : `https://${site.homepage}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textDecoration: "none",
                              color: "inherit",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {site.homepage}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}

              {site?.tenantInfo && (
                <Accordion disableGutters>
                  <AccordionSummary
                    expandIcon={<Icon icon="mdi:chevron-down" width={22} />}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.text.primary, 0.06),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon
                          icon="mdi:domain"
                          width={18}
                          color={theme.palette.text.secondary}
                        />
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        Geschäftsdaten
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" fontWeight={600}>
                        {site.tenantInfo.tenantName}
                      </Typography>
                      {(site.tenantInfo.address ||
                        site.tenantInfo.zip ||
                        site.tenantInfo.city) && (
                        <Typography variant="body2" color="text.secondary">
                          {[
                            site.tenantInfo.address,
                            [site.tenantInfo.zip, site.tenantInfo.city]
                              .filter(Boolean)
                              .join(" "),
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </Typography>
                      )}
                      {site.tenantInfo.phone && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Icon
                            icon="mdi:phone-outline"
                            width={15}
                            color={theme.palette.text.secondary}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {site.tenantInfo.phone}
                          </Typography>
                        </Stack>
                      )}
                      {site.tenantInfo.mail && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Icon
                            icon="mdi:email-outline"
                            width={15}
                            color={theme.palette.text.secondary}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {site.tenantInfo.mail}
                          </Typography>
                        </Stack>
                      )}
                      {site.tenantInfo.homepage && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Icon
                            icon="mdi:web"
                            width={15}
                            color={theme.palette.text.secondary}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="a"
                            href={
                              site.tenantInfo.homepage.startsWith("http")
                                ? site.tenantInfo.homepage
                                : `https://${site.tenantInfo.homepage}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textDecoration: "none",
                              color: "inherit",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {site.tenantInfo.homepage}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}

              {siteInfoText && (
                <Accordion disableGutters>
                  <AccordionSummary
                    expandIcon={<Icon icon="mdi:chevron-down" width={22} />}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.secondary.main, 0.08),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon
                          icon="mdi:text-box-outline"
                          width={18}
                          color={theme.palette.secondary.main}
                        />
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        Weitere Informationen
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: "pre-line" }}
                    >
                      {siteInfoText}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}
            </Paper>
          )}

          {/* Member type selection — card-based */}
          <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              fontWeight={700}
              sx={{ mb: 0.5 }}
            >
              Wer wird angemeldet?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Bitte wählen Sie aus, für wen Sie die Anmeldung ausfüllen möchten.
            </Typography>

            <Grid container spacing={2}>
              {memberTypeOptions.map((option) => {
                const selected = memberType === option.value;
                return (
                  <Grid item xs={12} sm={6} key={option.value}>
                    <Paper
                      variant="outlined"
                      onClick={() => setMemberType(option.value)}
                      sx={{
                        p: { xs: 2, sm: 2.5 },
                        cursor: "pointer",
                        border: "2px solid",
                        borderColor: selected ? "primary.main" : "divider",
                        borderRadius: 2,
                        transition:
                          "border-color 0.15s, background-color 0.15s",
                        bgcolor: selected
                          ? alpha(theme.palette.primary.main, 0.06)
                          : "background.paper",
                        position: "relative",
                        "&:hover": {
                          borderColor: selected
                            ? "primary.main"
                            : "primary.light",
                          bgcolor: selected
                            ? alpha(theme.palette.primary.main, 0.08)
                            : alpha(theme.palette.primary.main, 0.03),
                        },
                      }}
                    >
                      {selected && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            bgcolor: "primary.main",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Icon icon="mdi:check" width={15} color="white" />
                        </Box>
                      )}
                      <Stack spacing={1.5}>
                        <Box
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: 2,
                            bgcolor: selected
                              ? "primary.main"
                              : alpha(theme.palette.text.primary, 0.06),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: selected ? "white" : "text.secondary",
                            transition: "background-color 0.15s",
                          }}
                        >
                          <Icon icon={option.icon} width={28} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {option.label}
                          </Typography>
                          <Chip
                            label={option.ageHint}
                            size="small"
                            color={selected ? "primary" : "default"}
                            sx={{ mt: 0.5, mb: 0.75 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => handleSelectMemberType(memberType)}
                disabled={!memberType}
                fullWidth={isMobile}
                endIcon={<Icon icon="mdi:arrow-right" width={20} />}
                sx={{
                  minHeight: 52,
                  fontSize: "1rem",
                  px: 4,
                  minWidth: { sm: 280 },
                }}
              >
                Weiter zu den Mitgliedsdaten
              </Button>
              {!memberType && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  Bitte wählen Sie zuerst eine Mitgliedsart aus.
                </Typography>
              )}
            </Box>
          </Paper>

          {/* What to prepare */}
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 0.75 }}
            >
              <Icon
                icon="mdi:lightbulb-outline"
                width={16}
                color={theme.palette.info.main}
              />
              <Typography variant="caption" fontWeight={700}>
                Bereithalten:
              </Typography>
            </Stack>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 0.25, sm: 2 }}
              sx={{ pl: 0.5 }}
            >
              {[
                {
                  icon: "mdi:account-outline",
                  text: "Name, Geburtsdatum, Adresse",
                },
                { icon: "mdi:bank-outline", text: "IBAN (SEPA-Lastschrift)" },
                { icon: "mdi:email-outline", text: "E-Mail-Adresse" },
              ].map((item, i) => (
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  key={i}
                >
                  <Icon
                    icon={item.icon}
                    width={14}
                    color={theme.palette.text.disabled}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {item.text}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Stack>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* STEP 1 — MEMBER_FORM                                                  */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {step === steps.MEMBER_FORM && (
        <>
          <Grid container spacing={2} sx={contentWidthSx}>
            {/* Pricing panel */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={3}
                sx={{ p: 2.5, borderRadius: 2, height: "100%" }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Icon
                    icon="mdi:currency-eur"
                    width={22}
                    color={theme.palette.primary.main}
                  />
                  <Typography variant="h6" fontWeight={700}>
                    Beitrag & Laufzeit
                  </Typography>
                </Stack>

                {publicOptions.length > 1 && (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="public-option-label">
                      Tarif auswählen
                    </InputLabel>
                    <Select
                      labelId="public-option-label"
                      value={selectedOptionIndex}
                      label="Tarif auswählen"
                      onChange={(event) =>
                        setSelectedOptionIndex(Number(event.target.value))
                      }
                    >
                      {publicOptions.map((option, index) => (
                        <MenuItem
                          key={`${option.publicContribution}-${option.publicContractDuration}-${index}`}
                          value={index}
                        >
                          {option.publicContribution} EUR /{" "}
                          {option.publicContractDuration} Monate
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    border: "1px solid",
                    borderColor: alpha(theme.palette.primary.main, 0.15),
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="baseline"
                    sx={{ mb: 0.5 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Monatlicher Beitrag
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      color="primary.main"
                    >
                      {formatMoney(pricingPreview.discountedContribution)} €
                    </Typography>
                  </Stack>
                  {contributionDiscountLabel && (
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="caption" color="text.secondary">
                        Regulär: {formatMoney(pricingPreview.baseContribution)}{" "}
                        €
                      </Typography>
                      <Chip
                        label={`${contributionDiscountLabel} Rabatt`}
                        size="small"
                        color="success"
                        icon={<Icon icon="mdi:tag-outline" width={13} />}
                      />
                    </Stack>
                  )}
                  <Divider sx={{ my: 1.5 }} />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="baseline"
                  >
                    <Typography variant="body2" color="text.secondary">
                      Mindestlaufzeit
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {
                        publicOptions[selectedOptionIndex]
                          ?.publicContractDuration
                      }{" "}
                      Monate
                    </Typography>
                  </Stack>
                  {pricingPreview.baseEnrollmentFee > 0 && (
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="baseline"
                      sx={{ mt: 1 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Anmeldegebühr (einmalig)
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {formatMoney(pricingPreview.discountedEnrollmentFee)} €
                        {enrollmentFeeDiscountLabel && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 0.5 }}
                          >
                            (Rabatt: {enrollmentFeeDiscountLabel})
                          </Typography>
                        )}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Discount code panel */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={3}
                sx={{ p: 2.5, borderRadius: 2, height: "100%" }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <Icon
                    icon="mdi:tag-multiple-outline"
                    width={22}
                    color={theme.palette.secondary.main}
                  />
                  <Typography variant="h6" fontWeight={700}>
                    Rabattcode
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Optional: Haben Sie einen Rabattcode erhalten? Geben Sie ihn
                  hier ein.
                </Typography>

                <Stack spacing={2}>
                  {discountCodes.map((entry, index) => (
                    <Stack key={`discount-${index}`} spacing={0.75}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ xs: "stretch", sm: "center" }}
                      >
                        <TextField
                          label={
                            discountCodes.length > 1
                              ? `Rabattcode ${index + 1}`
                              : "Rabattcode"
                          }
                          value={entry.code}
                          onChange={(event) =>
                            handleDiscountCodeChange(index, event.target.value)
                          }
                          fullWidth
                          placeholder="z. B. SOMMER25"
                          InputProps={{
                            startAdornment: (
                              <Icon
                                icon="mdi:tag-outline"
                                width={18}
                                style={{
                                  marginRight: 8,
                                  color: theme.palette.text.secondary,
                                  flexShrink: 0,
                                }}
                              />
                            ),
                          }}
                        />
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleRemoveDiscountCode(index)}
                          disabled={discountCodes.length === 1}
                          fullWidth={isMobile}
                          sx={{ minWidth: { sm: 110 }, whiteSpace: "nowrap" }}
                        >
                          Entfernen
                        </Button>
                      </Stack>
                      {entry.status === "loading" && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ pl: 0.5 }}
                        >
                          Wird geprüft…
                        </Typography>
                      )}
                      {entry.message && entry.status !== "loading" && (
                        <Alert
                          severity={
                            entry.status === "valid"
                              ? "success"
                              : entry.status === "error"
                              ? "error"
                              : "warning"
                          }
                          sx={{ py: 0.5 }}
                        >
                          {entry.message}
                        </Alert>
                      )}
                    </Stack>
                  ))}
                  {discountCodes.length < 3 && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleAddDiscountCode}
                      startIcon={<Icon icon="mdi:plus" width={18} />}
                      fullWidth={isMobile}
                      sx={{ alignSelf: "flex-start" }}
                    >
                      Weiteren Code hinzufügen
                    </Button>
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          <Box sx={contentWidthSx}>
            {/* Body height hint */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.5, sm: 2 },
                my: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "info.light",
                background:
                  "linear-gradient(135deg, rgba(2, 136, 209, 0.08), rgba(255, 255, 255, 0.95))",
              }}
            >
              <Stack direction="row" spacing={0.5} alignItems="flex-start">
                <Icon
                  icon="mdi:human-male-height"
                  width={22}
                  color={theme.palette.info.main}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Warum fragen wir die Körpergröße?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Wir fragen die ungefähre Körpergröße ab, damit wir den
                    passenden Anzug in der richtigen Größe für das Mitglied
                    vorbereiten und korrekt ausgeben können.
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <PublicMemberForm
              key={`member-${members.length}`}
              memberData={currentMember}
              defaults={memberDefaults}
              readOnlyFields={readOnlyFields}
              contractDurationLabel="Mindestvertragslaufzeit in Monaten"
              onSubmit={handleMemberDataSubmit}
            />
          </Box>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* STEP 2 — ADD_ANOTHER (children only)                                  */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {step === steps.ADD_ANOTHER && (
        <Paper
          elevation={3}
          sx={{
            ...contentWidthSx,
            p: { xs: 2, sm: 3 },
            maxWidth: { xs: 360, sm: 680 },
            borderRadius: 3,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                bgcolor: "success.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                flexShrink: 0,
              }}
            >
              <Icon icon="mdi:check" width={26} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {members.length === 1
                  ? "1 Kind hinzugefügt"
                  : `${members.length} Kinder hinzugefügt`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {members.map((m) => `${m.firstName} ${m.lastName}`).join(", ")}
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ mb: 2.5 }} />

          <Typography variant="body1" sx={{ mb: 2.5 }}>
            Möchten Sie ein weiteres Kind für denselben Vertrag anmelden?
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleAddAnotherChild}
              fullWidth={isMobile}
              startIcon={<Icon icon="mdi:plus" width={18} />}
              sx={{ minHeight: 48 }}
            >
              Weiteres Kind hinzufügen
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleContinueToContract}
              fullWidth={isMobile}
              endIcon={<Icon icon="mdi:arrow-right" width={18} />}
              sx={{ minHeight: 48 }}
            >
              Weiter zu den Vertragsdaten
            </Button>
          </Stack>
        </Paper>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* STEP 3 — CONTRACT_FORM                                                */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {step === steps.CONTRACT_FORM && (
        <Box sx={contentWidthSx}>
          {/* Guardian notice for child registrations */}
          {memberType === "child" && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "warning.light",
                background:
                  "linear-gradient(135deg, rgba(237, 108, 2, 0.07), rgba(255, 255, 255, 0.97))",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Icon
                  icon="mdi:information-outline"
                  width={22}
                  color={theme.palette.warning.main}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="warning.dark"
                    sx={{ mb: 0.5 }}
                  >
                    Hinweis: Erziehungsberechtigter als Vertragsnehmer
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Bei minderjährigen Mitgliedern muss ein Elternteil oder
                    Erziehungsberechtigter als Vertragsnehmer eingetragen werden
                    — nicht das Kind. Bitte tragen Sie hier Ihre eigenen Daten
                    ein.
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          )}

          {/* SEPA info */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "info.light",
              background:
                "linear-gradient(135deg, rgba(2, 136, 209, 0.08), rgba(255, 255, 255, 0.97))",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Icon
                icon="mdi:bank-outline"
                width={22}
                color={theme.palette.info.main}
                style={{ marginTop: 2, flexShrink: 0 }}
              />
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ mb: 0.5 }}
                >
                  SEPA-Lastschriftmandat
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Für den Beitragseinzug benötigen wir Ihre IBAN. Der monatliche
                  Beitrag wird per Lastschrift von Ihrem Konto abgebucht. Ihre
                  Bankdaten werden verschlüsselt gespeichert.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <NewContractForm
            memberType={memberType}
            memberData={members[0] || {}}
            contractData={contractData}
            isPublicRegistration
            handleContractDataSubmit={handleContractDataSubmit}
          />
        </Box>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* STEP 4 — DOCUMENTS                                                    */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {step === steps.DOCUMENTS && (
        <Paper
          elevation={4}
          sx={{
            ...contentWidthSx,
            p: { xs: 2, sm: 3 },
            maxWidth: { xs: 360, sm: 960 },
            borderRadius: 3,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mb: 2.5 }}
          >
            <Icon
              icon="mdi:file-check-outline"
              width={28}
              color={theme.palette.primary.main}
            />
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Dokumente & Einwilligungen
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bitte lesen Sie die Dokumente durch und bestätigen Sie Ihre
                Zustimmung.
              </Typography>
            </Box>
          </Stack>

          {site?.tenantInfo && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 2.5,
                borderRadius: 1.5,
                bgcolor: "action.hover",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ display: "block", mb: 0.25 }}
              >
                Verantwortlicher (Datenschutz)
              </Typography>
              <Typography variant="body2">
                {site.tenantInfo.tenantName}
              </Typography>
              <Typography variant="body2">
                {site.tenantInfo.address}, {site.tenantInfo.zip}{" "}
                {site.tenantInfo.city}
              </Typography>
            </Paper>
          )}

          {/* Document links */}
          {documentLinks.length > 0 && (
            <>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                Dokumente lesen
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                Tippen Sie auf ein Dokument, um es zu öffnen und zu lesen.
              </Typography>
              {missingDocumentLabels.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  Folgende Dokumente sind derzeit nicht verfügbar:{" "}
                  {missingDocumentLabels.join(", ")}
                </Alert>
              )}
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                {documentLinks.map((item) => {
                  const url = getDocumentUrl(item.doc);
                  return (
                    <Grid item xs={12} sm={6} key={item.key}>
                      <Button
                        variant="outlined"
                        color="inherit"
                        fullWidth
                        component={Link}
                        href={url || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        disabled={!url}
                        startIcon={<Icon icon="mdi:file-pdf-box" width={20} />}
                        endIcon={<Icon icon="mdi:open-in-new" width={15} />}
                        sx={{
                          justifyContent: "space-between",
                          py: 1.25,
                          px: 2,
                          borderRadius: 1.5,
                          textTransform: "none",
                        }}
                      >
                        <Box sx={{ flex: 1, textAlign: "left" }}>
                          {item.label}
                        </Box>
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}

          {/* Consent checkboxes */}
          {publicRegistrationDocuments.length > 0 ? (
            <>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                Einwilligungen bestätigen
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                Bitte setzen Sie für jede Einwilligung einen Haken.
              </Typography>
              <Stack spacing={1} sx={{ mb: 3 }}>
                {publicRegistrationDocuments.map((doc) => (
                  <Paper
                    key={doc.docId}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      borderColor: docConsents[doc.docId]
                        ? "success.main"
                        : "divider",
                      bgcolor: docConsents[doc.docId]
                        ? alpha(theme.palette.success.main, 0.04)
                        : "background.paper",
                      transition: "border-color 0.15s, background-color 0.15s",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(docConsents[doc.docId])}
                          onChange={handleConsentToggle(doc.docId)}
                          color="success"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {doc.consentText || "Ich stimme dem Dokument zu."}
                        </Typography>
                      }
                      sx={{ m: 0, alignItems: "flex-start" }}
                    />
                  </Paper>
                ))}
              </Stack>
            </>
          ) : (
            <Alert severity="info" sx={{ mb: 3 }}>
              Für diese Anmeldung sind keine gesonderten Einwilligungen
              erforderlich.
            </Alert>
          )}

          <Divider sx={{ mb: 3 }} />

          {/* Digital signature */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 0.5 }}
          >
            <Icon
              icon="mdi:draw-pen"
              width={22}
              color={theme.palette.primary.main}
            />
            <Typography variant="h6" fontWeight={700}>
              Digitale Unterschrift
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Geben Sie Ihren vollständigen Namen ein, um die Anmeldung
            rechtsverbindlich zu unterzeichnen. Dies entspricht Ihrer
            handschriftlichen Unterschrift.
          </Typography>

          <TextField
            label="Vollständiger Name (Unterschrift)"
            value={typedName}
            onChange={(event) => setTypedName(event.target.value)}
            fullWidth
            required
            placeholder="Vor- und Nachname"
            helperText="Bitte geben Sie Ihren Namen so ein, wie Sie normalerweise unterschreiben."
            sx={{
              mb: 2,
              "& .MuiInputBase-input": {
                fontSize: "1.1rem",
                letterSpacing: "0.04em",
              },
            }}
            InputProps={{
              startAdornment: (
                <Icon
                  icon="mdi:pen"
                  width={20}
                  style={{
                    marginRight: 8,
                    color: theme.palette.text.secondary,
                    flexShrink: 0,
                  }}
                />
              ),
            }}
          />

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: 1.5,
              borderColor: signatureChecked ? "success.main" : "divider",
              bgcolor: signatureChecked
                ? alpha(theme.palette.success.main, 0.04)
                : "background.paper",
              transition: "border-color 0.15s, background-color 0.15s",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={signatureChecked}
                  onChange={(event) =>
                    setSignatureChecked(event.target.checked)
                  }
                  color="success"
                />
              }
              label={
                <Typography variant="body2">
                  Ich bestätige, dass alle eingetragenen Angaben korrekt und
                  vollständig sind, und schließe den Mitgliedsvertrag hiermit
                  verbindlich ab.
                </Typography>
              }
              sx={{ m: 0, alignItems: "flex-start" }}
            />
          </Paper>

          {consentError && (
            <Alert
              severity="error"
              icon={<Icon icon="mdi:alert-circle-outline" width={20} />}
              sx={{ mb: 2 }}
            >
              {consentError}
            </Alert>
          )}

          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth={isMobile}
            endIcon={<Icon icon="mdi:arrow-right" width={20} />}
            sx={{ minWidth: { xs: "100%", sm: 300 }, minHeight: 52 }}
            onClick={() => {
              const missingConsents = publicRegistrationDocuments.filter(
                (doc) => doc?.docId && !docConsents[doc.docId],
              );
              if (
                !typedName.trim() ||
                !signatureChecked ||
                missingConsents.length
              ) {
                setConsentError(
                  "Bitte alle Pflichtzustimmungen bestätigen und den Namen unterschreiben.",
                );
                return;
              }
              setConsentError(null);
              goToStep(steps.SUMMARY);
            }}
          >
            Weiter zur Zusammenfassung
          </Button>
        </Paper>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* STEP 5 — SUMMARY                                                      */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {step === steps.SUMMARY && (
        <Paper
          elevation={4}
          sx={{
            ...contentWidthSx,
            p: { xs: 2, sm: 3 },
            maxWidth: { xs: 360, sm: 960 },
            borderRadius: 3,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Icon
              icon="mdi:clipboard-check-outline"
              width={28}
              color={theme.palette.primary.main}
            />
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Zusammenfassung
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bitte prüfen Sie Ihre Angaben sorgfältig, bevor Sie die
                Anmeldung absenden.
              </Typography>
            </Box>
          </Stack>

          {/* Members */}
          <Box sx={{ mb: 3 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1.5 }}
            >
              <Icon
                icon="mdi:account-group"
                width={20}
                color={theme.palette.text.secondary}
              />
              <Typography variant="h6" fontWeight={700}>
                {members.length === 1
                  ? "Mitglied"
                  : `Mitglieder (${members.length})`}
              </Typography>
            </Stack>
            <Grid container spacing={1.5}>
              {members.map((member, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  key={`${member.memberNumber}-${index}`}
                >
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          bgcolor: "primary.main",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon icon="mdi:account" width={22} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={700} noWrap>
                          {[member.title, member.firstName, member.lastName]
                            .filter(Boolean)
                            .join(" ")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Geb.: {getDisplayDate(member.birthdate)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Nr.: {member.memberNumber}
                        </Typography>
                        {member.bodyHeightCm && (
                          <Typography variant="body2" color="text.secondary">
                            Größe: {formatBodyHeight(member.bodyHeightCm)}
                          </Typography>
                        )}
                        {gradArray[member.degree]?.label && (
                          <Typography variant="body2" color="text.secondary">
                            Gürtelgrad: {gradArray[member.degree].label}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Contract data */}
          <Box sx={{ mb: 3 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1.5 }}
            >
              <Icon
                icon="mdi:account-tie-outline"
                width={20}
                color={theme.palette.text.secondary}
              />
              <Typography variant="h6" fontWeight={700}>
                Vertragsnehmer
              </Typography>
            </Stack>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.75 }}
                  >
                    Persönliche Daten
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2" fontWeight={600}>
                      {[
                        contractData.title,
                        contractData.firstName,
                        contractData.lastName,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    </Typography>
                    {contractData.birthdate && (
                      <Typography variant="body2" color="text.secondary">
                        Geb.: {getDisplayDate(contractData.birthdate)}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {[
                        contractData.street,
                        [contractData.zip, contractData.city]
                          .filter(Boolean)
                          .join(" "),
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.75 }}
                  >
                    Kontakt & Bankverbindung
                  </Typography>
                  <Stack spacing={0.5}>
                    {contractData.email && (
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Icon
                          icon="mdi:email-outline"
                          width={15}
                          color={theme.palette.text.secondary}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {contractData.email}
                        </Typography>
                      </Stack>
                    )}
                    {(contractData.phoneNumber || contractData.mobile) && (
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Icon
                          icon="mdi:phone-outline"
                          width={15}
                          color={theme.palette.text.secondary}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {contractData.phoneNumber || contractData.mobile}
                        </Typography>
                      </Stack>
                    )}
                    {contractData.iban && (
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Icon
                          icon="mdi:bank-outline"
                          width={15}
                          color={theme.palette.text.secondary}
                        />
                        <Typography variant="body2" color="text.secondary">
                          IBAN: {contractData.iban}
                        </Typography>
                      </Stack>
                    )}
                    {contractData.owner && (
                      <Typography variant="body2" color="text.secondary">
                        Kontoinhaber: {contractData.owner}
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Pricing summary */}
          <Box sx={{ mb: 3 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1.5 }}
            >
              <Icon
                icon="mdi:currency-eur"
                width={20}
                color={theme.palette.text.secondary}
              />
              <Typography variant="h6" fontWeight={700}>
                Beitrag
              </Typography>
            </Stack>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.03),
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Monatlicher Beitrag</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatMoney(pricingPreview.baseContribution)} €
                  </Typography>
                </Stack>
                {contributionDiscountLabel && (
                  <>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="success.main">
                        Rabatt ({contributionDiscountLabel})
                      </Typography>
                      <Typography
                        variant="body2"
                        color="success.main"
                        fontWeight={600}
                      >
                        −{" "}
                        {formatMoney(
                          pricingPreview.baseContribution -
                            pricingPreview.discountedContribution,
                        )}{" "}
                        €
                      </Typography>
                    </Stack>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" fontWeight={700}>
                        Ihr Beitrag
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color="primary.main"
                      >
                        {formatMoney(pricingPreview.discountedContribution)} €
                      </Typography>
                    </Stack>
                  </>
                )}
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Mindestlaufzeit
                  </Typography>
                  <Typography variant="body2">
                    {publicOptions[selectedOptionIndex]?.publicContractDuration}{" "}
                    Monate
                  </Typography>
                </Stack>
                {pricingPreview.baseEnrollmentFee > 0 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Anmeldegebühr (einmalig)
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatMoney(pricingPreview.discountedEnrollmentFee)} €
                      {enrollmentFeeDiscountLabel && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          {" "}
                          (Rabatt: {enrollmentFeeDiscountLabel})
                        </Typography>
                      )}
                    </Typography>
                  </Stack>
                )}
                {discountSummary.codes.length > 0 && (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Typography variant="caption" color="text.secondary">
                      Rabattcodes:
                    </Typography>
                    {discountSummary.codes.map((code) => (
                      <Chip
                        key={code}
                        label={code}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Box>

          {/* Consents summary */}
          {publicRegistrationDocuments.length > 0 && (
            <>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ mb: 3 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1.5 }}
                >
                  <Icon
                    icon="mdi:file-check-outline"
                    width={20}
                    color={theme.palette.text.secondary}
                  />
                  <Typography variant="h6" fontWeight={700}>
                    Einwilligungen
                  </Typography>
                </Stack>
                <Stack spacing={0.75}>
                  {publicRegistrationDocuments.map((doc) => (
                    <Stack
                      key={doc.docId}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                    >
                      <Icon
                        icon={
                          docConsents[doc.docId]
                            ? "mdi:check-circle"
                            : "mdi:close-circle"
                        }
                        width={18}
                        color={
                          docConsents[doc.docId]
                            ? theme.palette.success.main
                            : theme.palette.error.main
                        }
                        style={{ marginTop: 2, flexShrink: 0 }}
                      />
                      <Typography variant="body2">
                        {doc.consentText || doc.title || doc.fileName}
                      </Typography>
                    </Stack>
                  ))}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Icon
                      icon="mdi:pen"
                      width={18}
                      color={theme.palette.text.secondary}
                      style={{ flexShrink: 0 }}
                    />
                    <Typography variant="body2">
                      Unterschrift: <strong>{typedName || "—"}</strong>
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </>
          )}

          {/* Submission notice */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.warning.main, 0.04),
              borderColor: "warning.light",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Icon
                icon="mdi:information-outline"
                width={20}
                color={theme.palette.warning.main}
                style={{ marginTop: 2, flexShrink: 0 }}
              />
              <Typography variant="body2" color="text.secondary">
                Mit dem Klick auf „Kostenpflichtig registrieren" senden Sie Ihre
                Anmeldung verbindlich ab. Der monatliche Beitrag wird per
                SEPA-Lastschrift eingezogen.
              </Typography>
            </Stack>
          </Paper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth={isMobile}
            sx={{
              minWidth: { xs: "100%", sm: 320 },
              minHeight: 56,
              fontSize: "1rem",
            }}
            disabled={submitting}
            onClick={handleSubmitRegistration}
            endIcon={submitting ? null : <Icon icon="mdi:send" width={20} />}
          >
            {submitting ? "Wird gesendet…" : "Kostenpflichtig registrieren"}
          </Button>
        </Paper>
      )}
    </Box>
  );
}

export default PublicRegistrationPage;
